import React, { useState, useEffect, useCallback } from 'react';
import { profitabilityService } from '../services/externalCostsService';
export default function ProfitabilityPage() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  // Dane
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [trend, setTrend] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationDetails, setLocationDetails] = useState(null);

  // Filtry dat
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const [dateFrom, setDateFrom] = useState(firstDay.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(today.toISOString().split('T')[0]);

  // Pobierz podsumowanie
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await profitabilityService.getSummary(dateFrom, dateTo);
      if (response.success) {
        setSummary(response.data);
      }
    } catch (error) {
      console.error('Blad pobierania podsumowania:', error);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  // Pobierz porownanie
  const fetchComparison = useCallback(async () => {
    try {
      const response = await profitabilityService.getComparison(dateFrom, dateTo);
      if (response.success) {
        setComparison(response.data);
      }
    } catch (error) {
      console.error('Blad pobierania porownania:', error);
    }
  }, [dateFrom, dateTo]);

  // Pobierz trend
  const fetchTrend = useCallback(async () => {
    try {
      const response = await profitabilityService.getTrend(selectedLocation, 6);
      if (response.success) {
        setTrend(response.data);
      }
    } catch (error) {
      console.error('Blad pobierania trendu:', error);
    }
  }, [selectedLocation]);

  // Pobierz szczegoly lokalizacji
  const fetchLocationDetails = useCallback(async (locationId) => {
    try {
      const response = await profitabilityService.getLocationProfitability(locationId, dateFrom, dateTo);
      if (response.success) {
        setLocationDetails(response.data);
      }
    } catch (error) {
      console.error('Blad pobierania szczeg. lokalizacji:', error);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchSummary();
    fetchComparison();
  }, [fetchSummary, fetchComparison]);

  useEffect(() => {
    fetchTrend();
  }, [fetchTrend]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'dodatnia': return { bg: 'var(--success-light)', text: 'var(--success)' };
      case 'ujemna': return { bg: 'var(--danger-light)', text: 'var(--danger)' };
      default: return { bg: 'var(--warning-light)', text: 'var(--warning)' };
    }
  };

  const handleLocationClick = (locationId) => {
    setSelectedLocation(locationId);
    fetchLocationDetails(locationId);
    setActiveTab('details');
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>Rentownosc</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          />
          <span>-</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid var(--border)', paddingBottom: '10px' }}>
        {[
          { id: 'summary', label: 'Podsumowanie', icon: '📊' },
          { id: 'comparison', label: 'Porownanie lokalizacji', icon: '📈' },
          { id: 'trend', label: 'Trend', icon: '📉' },
          { id: 'details', label: 'Szczegoly', icon: '🔍', disabled: !selectedLocation }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: activeTab === tab.id ? 'var(--primary)' : 'var(--card-bg)',
              border: activeTab === tab.id ? '1px solid var(--primary)' : '1px solid var(--border)',
              // disabled: text-muted + opacity zamiast var(--border) (bylo niewidoczne na jasnym tle)
              color: activeTab === tab.id ? '#fff' : tab.disabled ? 'var(--text-muted)' : 'var(--text-secondary)',
              opacity: tab.disabled ? 0.55 : 1,
              cursor: tab.disabled ? 'not-allowed' : undefined,
              fontWeight: activeTab === tab.id ? '600' : '500'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>Ladowanie danych...</div>
      ) : (
        <>
          {/* Podsumowanie */}
          {activeTab === 'summary' && summary && (
            <div>
              {/* Glowne KPI */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '25px', borderRadius: '16px' }}>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Calkowita marza netto</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px' }}>{formatCurrency(summary.total?.marza_netto)}</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #f093fb 0%, var(--danger) 100%)', color: '#fff', padding: '25px', borderRadius: '16px' }}>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Koszty zewnetrzne</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px' }}>{formatCurrency(summary.total?.koszty_zewnetrzne)}</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff', padding: '25px', borderRadius: '16px' }}>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Faktury zakupowe</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px' }}>{formatCurrency(summary.total?.faktury_zakupowe)}</div>
                </div>
                <div style={{
                  background: summary.total?.status === 'dodatnia'
                    ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                    : 'linear-gradient(135deg, var(--danger) 0%, var(--danger) 100%)',
                  color: '#fff', padding: '25px', borderRadius: '16px'
                }}>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>RENTOWNOSC</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px' }}>{formatCurrency(summary.total?.rentownosc)}</div>
                  <div style={{ fontSize: '16px', marginTop: '5px' }}>{formatPercent(summary.total?.rentownosc_procent)}</div>
                </div>
              </div>

              {/* Lista lokalizacji */}
              <div style={{ background: 'var(--card-bg)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid var(--border)' }}>
                  <h3 style={{ margin: 0 }}>Rentownosc per lokalizacja</h3>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Lokalizacja</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Marza netto</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Koszty zewn.</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Faktury zakupowe</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Rentownosc</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.locations?.map((loc, index) => {
                      const statusColor = getStatusColor(loc.status);
                      return (
                        <tr
                          key={loc.location_id}
                          onClick={() => handleLocationClick(loc.location_id)}
                          style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}
                        >
                          <td style={{ padding: '12px' }}>
                            <div style={{ fontWeight: '500' }}>{loc.location_name}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{loc.kod}</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--success)', fontWeight: '500' }}>{formatCurrency(loc.marza_netto)}</td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--danger)' }}>{formatCurrency(loc.koszty_zewnetrzne)}</td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--warning)' }}>{formatCurrency(loc.faktury_zakupowe)}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', fontSize: '16px' }}>{formatCurrency(loc.rentownosc)}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              background: statusColor.bg,
                              color: statusColor.text,
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {formatPercent(loc.rentownosc_procent)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Porownanie */}
          {activeTab === 'comparison' && comparison && (
            <div>
              {/* Statystyki */}
              {comparison.stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                  <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Najlepsza lokalizacja</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--success)' }}>{comparison.stats.najlepsza}</div>
                  </div>
                  <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Najgorsza lokalizacja</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--danger)' }}>{comparison.stats.najgorsza}</div>
                  </div>
                  <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Srednia rentownosc</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(comparison.stats.srednia_rentownosc)}</div>
                  </div>
                </div>
              )}

              {/* Ranking */}
              <div style={{ background: 'var(--card-bg)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid var(--border)' }}>
                  <h3 style={{ margin: 0 }}>Ranking lokalizacji</h3>
                </div>
                {comparison.comparison?.map((loc, index) => {
                  const maxRentownosc = Math.max(...comparison.comparison.map(l => Math.abs(l.rentownosc)));
                  const barWidth = maxRentownosc > 0 ? (Math.abs(loc.rentownosc) / maxRentownosc * 100) : 0;
                  const isPositive = loc.rentownosc >= 0;

                  return (
                    <div
                      key={loc.location_id}
                      onClick={() => handleLocationClick(loc.location_id)}
                      style={{
                        padding: '15px 20px',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                      }}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: index < 3 ? 'var(--warning)' : 'var(--border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        color: index < 3 ? '#000' : 'var(--text-secondary)'
                      }}>
                        {loc.ranking}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500' }}>{loc.nazwa}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          Sprzedaz: {formatCurrency(loc.sprzedaz)} | Marza: {formatPercent(loc.marza_procent)}
                        </div>
                        <div style={{ marginTop: '8px', height: '8px', background: 'var(--bg)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${barWidth}%`,
                            background: isPositive ? 'var(--success)' : 'var(--danger)',
                            borderRadius: '4px'
                          }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: isPositive ? 'var(--success)' : 'var(--danger)' }}>
                          {formatCurrency(loc.rentownosc)}
                        </div>
                        <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{formatPercent(loc.rentownosc_procent)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Trend */}
          {activeTab === 'trend' && trend && (
            <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginTop: 0 }}>Trend rentownosci (ostatnie 6 miesiecy)</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {trend.trend?.map((month, index) => {
                  const maxVal = Math.max(...trend.trend.map(m => Math.abs(m.rentownosc)));
                  const barWidth = maxVal > 0 ? (Math.abs(month.rentownosc) / maxVal * 100) : 0;
                  const isPositive = month.rentownosc >= 0;

                  return (
                    <div key={month.miesiac} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ width: '100px', fontWeight: '500' }}>{month.nazwa_miesiaca}</div>
                      <div style={{ flex: 1, height: '30px', background: 'var(--bg)', borderRadius: '6px', overflow: 'hidden', position: 'relative' }}>
                        <div style={{
                          position: 'absolute',
                          height: '100%',
                          width: `${barWidth}%`,
                          background: isPositive
                            ? 'linear-gradient(90deg, var(--success), var(--success))'
                            : 'linear-gradient(90deg, var(--danger), var(--orange))',
                          borderRadius: '6px'
                        }} />
                      </div>
                      <div style={{ width: '150px', textAlign: 'right' }}>
                        <span style={{ fontWeight: 'bold', color: isPositive ? 'var(--success)' : 'var(--danger)' }}>
                          {formatCurrency(month.rentownosc)}
                        </span>
                        <span style={{ marginLeft: '10px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                          {formatPercent(month.rentownosc_procent)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Szczegoly lokalizacji */}
          {activeTab === 'details' && locationDetails && (
            <div>
              <h2 style={{ marginBottom: '20px' }}>{locationDetails.location_name}</h2>

              {/* Przychody */}
              <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0, borderBottom: '2px solid var(--success)', paddingBottom: '10px', color: 'var(--success)' }}>Przychody</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Sprzedaz brutto</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{formatCurrency(locationDetails.przychody?.sprzedaz_brutto)}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Koszt zakupu sprzedanych</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--danger)' }}>{formatCurrency(locationDetails.przychody?.koszt_zakupu_sprzedanych)}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Marza netto</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--success)' }}>{formatCurrency(locationDetails.przychody?.marza_netto)}</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{formatPercent(locationDetails.przychody?.marza_procent)}</div>
                  </div>
                </div>
                <div style={{ marginTop: '20px', display: 'flex', gap: '30px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  <div>Z paragonow: {formatCurrency(locationDetails.przychody?.z_paragonow?.marza)} ({locationDetails.przychody?.z_paragonow?.transakcje} transakcji)</div>
                  <div>Z faktur: {formatCurrency(locationDetails.przychody?.z_faktur?.marza)} ({locationDetails.przychody?.z_faktur?.faktury} faktur)</div>
                </div>
              </div>

              {/* Koszty */}
              <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0, borderBottom: '2px solid var(--danger)', paddingBottom: '10px', color: 'var(--danger)' }}>Koszty</h3>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontWeight: '500', marginBottom: '10px' }}>Koszty zewnetrzne: {formatCurrency(locationDetails.koszty?.zewnetrzne?.suma)}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                    {Object.entries(locationDetails.koszty?.zewnetrzne?.per_category || {}).map(([nazwa, data]) => (
                      <div key={nazwa} style={{ padding: '10px', background: 'var(--bg)', borderRadius: '6px', borderLeft: `3px solid ${data.kolor || 'var(--text-secondary)'}` }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{nazwa}</div>
                        <div style={{ fontWeight: 'bold' }}>{formatCurrency(data.brutto)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div style={{ fontWeight: '500' }}>Faktury zakupowe: {formatCurrency(locationDetails.koszty?.faktury_zakupowe?.suma)}</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>({locationDetails.koszty?.faktury_zakupowe?.liczba} faktur)</div>
                </div>

                <div style={{ marginTop: '20px', padding: '15px', background: 'var(--warning-light)', borderRadius: '8px' }}>
                  <strong>Suma wszystkich kosztow: {formatCurrency(locationDetails.koszty?.suma_kosztow)}</strong>
                </div>
              </div>

              {/* Rentownosc */}
              <div style={{
                background: locationDetails.rentownosc?.status === 'dodatnia'
                  ? 'linear-gradient(135deg, var(--success-light) 0%, var(--success-light) 100%)'
                  : 'linear-gradient(135deg, var(--danger-light) 0%, var(--danger-light) 100%)',
                padding: '30px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '18px', marginBottom: '10px' }}>RENTOWNOSC LOKALIZACJI</div>
                <div style={{ fontSize: '48px', fontWeight: 'bold', color: locationDetails.rentownosc?.status === 'dodatnia' ? 'var(--success)' : 'var(--danger)' }}>
                  {formatCurrency(locationDetails.rentownosc?.kwota)}
                </div>
                <div style={{ fontSize: '24px', marginTop: '10px' }}>
                  {formatPercent(locationDetails.rentownosc?.procent)}
                </div>
                <div style={{ fontSize: '14px', marginTop: '15px', color: 'var(--text-secondary)' }}>
                  Po uwzglednieniu faktur do zaplaty: {formatCurrency(locationDetails.rentownosc?.kwota_po_fakturach)}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
